import { useCallback, useState } from "react";
import axios from "axios";
import { mapPreFinanceReadToOverviewRow } from "../utils/preFinanceCodeUtils";
import { usePermissions } from "./usePermissions";
import { CAPABILITIES } from "../auth/permissions";

export const usePreFinanceDossierDialog = () => {
  const { hasPermission } = usePermissions();
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [dialogContext, setDialogContext] = useState({ customerId: null, customerNumber: null });
  const [loadingPreFinanceId, setLoadingPreFinanceId] = useState(null);
  const [error, setError] = useState("");

  const canCreatePreFinance = hasPermission(CAPABILITIES.prefinanceCreate, "X");
  const canFinalizePreFinance = hasPermission(CAPABILITIES.prefinanceFinalize, "X");
  const canUnfinalizePreFinance = hasPermission(CAPABILITIES.prefinanceUnfinalize, "X");

  const openDossierById = useCallback(async (preFinanceId, context = {}) => {
    if (!preFinanceId || preFinanceId <= 0) {
      return;
    }

    setLoadingPreFinanceId(preFinanceId);
    setError("");
    setDialogContext({
      customerId: context.customerId ?? null,
      customerNumber: context.customerNumber ?? null,
    });

    try {
      const response = await axios.get(`/api/v1/preFinance/${preFinanceId}`);
      const dossier = mapPreFinanceReadToOverviewRow(response.data);
      setSelectedDossier(dossier);
      setDialogContext({
        customerId: context.customerId ?? dossier?.entityId ?? null,
        customerNumber: context.customerNumber ?? null,
      });
    } catch (requestError) {
      console.error("Unable to load prefinance dossier:", requestError);
      setError(
        requestError.response?.data?.message ||
          `Unable to load prefinance dossier ${preFinanceId}.`
      );
    } finally {
      setLoadingPreFinanceId(null);
    }
  }, []);

  const closeDossier = useCallback(() => {
    setSelectedDossier(null);
    setDialogContext({ customerId: null, customerNumber: null });
    setError("");
  }, []);

  return {
    selectedDossier,
    dialogContext,
    loadingPreFinanceId,
    error,
    setError,
    openDossierById,
    closeDossier,
    canCreatePreFinance,
    canFinalizePreFinance,
    canUnfinalizePreFinance,
  };
};
